import {createSnackbar} from 'Core/ducks/snackbar';
import {AsyncResult, Loaded} from 'Core/models/Async';
import {QuotingRouteConstants} from 'Quoting/Routes';
import QuoteActionAPI from 'Quoting/api/QuoteActionAPI';
import {getOpportunitySummary, setCopyQuoteAlertMessage} from 'Quoting/ducks/opportunity';
import {
  QuoteActionType,
  clearSelectedQuotesForAction,
  copyQuote,
  createProposal,
  declineQuote,
  evaluateDecisionTable,
  generateProposal,
  prepareForProposal,
  retrieveQuotesForGenerateProposal,
  udpateQuotesForSelectedProposal,
  updateDecisionTableHeapedResults,
  updateDecisionTableLevelResults,
  updateProposalSelectedForGeneration,
} from 'Quoting/ducks/quoteActions';
import {DecisionHeapedTable, DecisionLevelTable} from 'Quoting/models/DTOs/CompensationPayload';
import {ProposalQuoteDetails, ProposalResponseDTO} from 'Quoting/models/DTOs/Proposal';
import {MasterPolicyQuote} from 'Quoting/models/DTOs/quote';
import {push} from 'connected-react-router';
import {partitionMap} from 'fp-ts/lib/Array';
import {isRight} from 'fp-ts/lib/Either';
import {pipe} from 'fp-ts/lib/pipeable';
import {all, call, delay, put, select, takeLatest} from 'redux-saga/effects';
import {ReducerState} from 'rootReducer';
import {retreivePoliciesWithRates} from './commonQuotingSagaMethods';
import {fetchOpportunityQuoteListFromAPI, fetchOpportunitySummaryFromAPI} from './opportunitySaga';

function* prepareQuotesForProposal(action: ReturnType<typeof prepareForProposal>) {
  const errorMessage = 'Error: Failed to prepare quote(s) for proposal.';
  try {
    const prepareForProposalAPICalls = action.payload.selectedQuotes?.map(quote =>
      call(QuoteActionAPI.prepareForProposal, quote)
    );

    yield put(createSnackbar('Preparing for proposal...'));
    const responses: Array<AsyncResult<MasterPolicyQuote>> = yield all(prepareForProposalAPICalls);

    const {left: failures} = pipe(
      responses,
      partitionMap(x => x)
    );

    if (failures.length > 0) {
      yield put(createSnackbar(errorMessage));
    }

    yield call(
      fetchOpportunityQuoteListFromAPI,
      action.payload.opportunityRootId,
      action.payload.opportunityRevisionNo
    );
  } catch (error) {
    console.error(error);
    yield put(createSnackbar(errorMessage));
  } finally {
    yield put(clearSelectedQuotesForAction());
  }
}

function* createProposalForQuotes(action: ReturnType<typeof createProposal>) {
  const errorMessage = 'Error: Failed to create proposal for selected quotes.';
  try {
    yield put(createSnackbar('Creating New Proposal...'));
    const response: AsyncResult<ProposalResponseDTO> = yield call(
      QuoteActionAPI.createProposal,
      action.payload.opportunityRootId,
      action.payload.selectedQuotes,
      action.payload.newProposalData
    );
    if (isRight(response)) {
      yield put(updateProposalSelectedForGeneration(new Loaded(response.right)));
      yield put(
        push(
          QuotingRouteConstants.GENERATE_PROPOSAL(
            action.payload.opportunityNumber,
            action.payload.customerNumber,
            response.right._key?.rootId,
            String(response.right._key?.revisionNo)
          )
        )
      );
    }
  } catch (error) {
    console.error(error);
    yield put(createSnackbar(errorMessage));
  }
}

function* declineSelectedQuotes(action: ReturnType<typeof declineQuote>) {
  const errorMessage = 'Error: Failed to decline quote(s).';
  try {
    const declineQuoteAPICalls = action.payload.selectedQuotes?.map(quote =>
      call(QuoteActionAPI.declineQuote, quote, action.payload.quoteDeclineDetails)
    );
    const responses: Array<AsyncResult<MasterPolicyQuote>> = yield all(declineQuoteAPICalls);

    const {left: failures} = pipe(
      responses,
      partitionMap(x => x)
    );

    if (failures.length > 0) {
      yield put(createSnackbar(errorMessage));
    }

    yield call(
      fetchOpportunityQuoteListFromAPI,
      action.payload.opportunityRootId,
      action.payload.opportunityRevisionNo
    );
  } catch (error) {
    console.error(error);
    yield put(createSnackbar(errorMessage));
  } finally {
    yield put(clearSelectedQuotesForAction());
  }
}

function* copyQuoteWithDetails(action: ReturnType<typeof copyQuote>) {
  const errorMessage = 'Error: Failed to copy quote.';
  try {
    const selectedOpportunityId: string = yield select(
      (state: ReducerState) => state.opportunity.selectedOpportunityId
    );

    if (action.payload.selectedQuotes?.length <= 0) {
      return;
    }

    const response: AsyncResult<MasterPolicyQuote> = yield call(
      QuoteActionAPI.copyQuote,
      action.payload.selectedQuotes[0],
      action.payload.copyQuoteDetails
    );

    if (isRight(response) && response.right?.policyNumber) {
      yield put(
        setCopyQuoteAlertMessage(`Successfully Copied. New Quote ID: ${response.right?.policyNumber}`, 'success')
      );
    } else {
      yield put(createSnackbar(errorMessage));
    }

    if (selectedOpportunityId) {
      yield delay(1000);
      yield call(fetchOpportunitySummaryFromAPI, getOpportunitySummary(selectedOpportunityId));
    }
  } catch (error) {
    console.error(error);
    yield put(setCopyQuoteAlertMessage(errorMessage, 'error'));
  } finally {
    yield put(clearSelectedQuotesForAction());
  }
}

function* retreiveQuotesForSelectedProposal(action: ReturnType<typeof retrieveQuotesForGenerateProposal>) {
  const errorMessage = 'Error: could not fetch quotes linked to proposal.';
  try {
    const response: AsyncResult<ProposalQuoteDetails[]> = yield call(
      retreivePoliciesWithRates,
      action.payload.quoteLinks
    );

    if (isRight(response)) {
      yield put(udpateQuotesForSelectedProposal(response.right));
    } else {
      yield put(udpateQuotesForSelectedProposal([]));
      yield put(createSnackbar(errorMessage));
    }
  } catch (error) {
    console.error(error);
    yield put(udpateQuotesForSelectedProposal([]));
    yield put(createSnackbar(errorMessage));
  }
}

function* generateProposalAPI(action: ReturnType<typeof generateProposal>) {
  const errorMessage = 'Error: Failed to generate proposal.';
  try {
    yield put(createSnackbar('Generating Proposal...'));
    const response: AsyncResult<any> = yield call(
      QuoteActionAPI.generateProposal,
      action.payload.proposalRootId,
      action.payload.proposalRevisionNo
    );
    if (isRight(response)) {
      yield put(createSnackbar('Successfully Generated...'));
      yield put(push(QuotingRouteConstants.OPPORTUNITY_SUMMARY(action.payload.opportunityId)));
    } else {
      yield put(createSnackbar(errorMessage));
      yield put(push(QuotingRouteConstants.OPPORTUNITY_SUMMARY(action.payload.opportunityId)));
    }
  } catch (error) {
    console.error(error);
    yield put(createSnackbar(errorMessage));
  }
}

function* evaluateDecisionTableFromAPI(action: ReturnType<typeof evaluateDecisionTable>) {
  const errorMessage = 'Error: Failed to evaluate decision table';
  const {payloadLevel, productCd, payloadHeaped, onComplete} = action.payload;
  try {
    const response: [AsyncResult<DecisionLevelTable[]>, AsyncResult<DecisionHeapedTable[]>] = yield call(
      QuoteActionAPI.evaluateDecisionTable,
      payloadLevel,
      payloadHeaped,
      productCd
    );

    if (isRight(response[0])) {
      yield put(updateDecisionTableLevelResults(response[0].right));
    }
    if (isRight(response[1])) {
      yield put(updateDecisionTableHeapedResults(response[1].right));
    }
    if (onComplete && isRight(response[0]) && isRight(response[1])) {
      onComplete({levelEqu: response[0].right[0].maxCommissionLevel.toString(), heapedValues: response[1].right});
    }
  } catch (error) {
    console.error(error);
    yield put(createSnackbar(errorMessage));
    action.payload.onComplete?.({levelEqu: '', heapedValues: []});
  }
}

function* evaluateDecisionTableSaga() {
  yield takeLatest(QuoteActionType.EVALUATE_DECISION_TABLE, evaluateDecisionTableFromAPI);
}

function* prepareQuotesForProposalSaga() {
  yield takeLatest(QuoteActionType.PREPARE_FOR_PROPOSAL, prepareQuotesForProposal);
}

function* createPropsalForQuotesSaga() {
  yield takeLatest(QuoteActionType.CREATE_PROPOSAL, createProposalForQuotes);
}

function* declineSelectedQuotesSaga() {
  yield takeLatest(QuoteActionType.DECLINE_QUOTE, declineSelectedQuotes);
}

function* retreiveQuotesForSelectedProposalSaga() {
  yield takeLatest(QuoteActionType.RETRIEVE_QUOTES_FOR_GENERATE_PROPOSAL, retreiveQuotesForSelectedProposal);
}

function* generateProposalSaga() {
  yield takeLatest(QuoteActionType.GENERATE_PROPOSAL, generateProposalAPI);
}

function* copyQuoteSaga() {
  yield takeLatest(QuoteActionType.COPY_QUOTE, copyQuoteWithDetails);
}

export default function* quoteActionSaga() {
  yield all([
    prepareQuotesForProposalSaga(),
    createPropsalForQuotesSaga(),
    declineSelectedQuotesSaga(),
    retreiveQuotesForSelectedProposalSaga(),
    generateProposalSaga(),
    copyQuoteSaga(),
    evaluateDecisionTableSaga(),
  ]);
}
